import {
  type ActiveFiltersState,
  type AdvancedFilterState,
  type EntitlementRiskObject,
  type InsightItemTypes,
  type ListTrackerState,
} from '../../../shared/types';

export interface EntitlementDetailsRowType {
  entitlementValueKey: number;
  entitlementDisplayName: string | null;
  entitlementDescription: string | null;
  entitlementTypeKey: number;
  entitlementTypeName: string | null;
  entitlementValue: string;
  entitlementSoxSeverity: string | null;
  entitlementSysSeverity: string | null;
  entitlementPrivilegeSeverity: string | null;
  endpointKey?: number | null;
  endpointName: string;
  endPointLogo: string | null;
  riskObjects: EntitlementRiskObject[];
  riskCount: number;
  lastUpdated: string;
  createdDate?: string | null;
  violationCount: number;
  userCount: number;
  entitlementGlossory?: string | null;
  glossary?: string | null;
  riskIds?: number[];
  userIds?: number[];
  customProperty1?: string | null;
}

/**
 * Cache state for filter options to improve performance
 * Stores filter data with metadata for efficient retrieval
 */
export interface FilterCacheState {
  entries: Record<string, Record<string, unknown>>;
  ids: Record<string, Record<string, string[]>>;
  metadata: Record<string, { lastfetch: number }>;
}

export enum EntitlementType {
  SLICE_NAME = 'aag/entitlement',
  API_SLICE_NAME = 'aag/entitlementTabApi',
}

export interface TrackedEntitlementRowType extends EntitlementDetailsRowType {
  tracker: ListTrackerState;
}

export interface EntitlementPagination {
  max: number;
  offset: number;
}

export interface EntitlementColumnState {
  label: string;
  value: string;
  isChecked: boolean;
  isDisabled: boolean;
}

export type EntitlementSortState = Record<'value' | 'order', string> | null;

interface EntitlementControlsState {
  filters: AdvancedFilterState[];
  isInsightsOpen: boolean;
  activeFilters: ActiveFiltersState[];
  sortInAction: EntitlementSortState;
  activeColumns: EntitlementColumnState[];
  filterCache: FilterCacheState;
  searchKey: string | null;
  activeInsight: InsightItemTypes | null;
  activeRowContext: EntitlementSidePanelRowContextState | null;
}

export interface EntitlementListState {
  isListLoading: boolean;
  totalCount: number | null;
  rowIdToTransition: number | string | null;
  data: EntitlementDetailsRowType[];
  displayRecords: number;
  isRefresh: boolean;
}

interface EntitlementBulkActionState {
  isFooterVisible: boolean;
  bulkCount: number;
  isBulkChecked: boolean;
}

export interface EntitlementSidePanelRowContextState {
  id: string;
  riskCount?: number;
  userCount?: number;
  violationCount?: number;
  entitlementDisplayName?: string;
  entitlementValue?: string;
  endPointLogo?: string;
  endpointName?: string;
  entitlementTypeName?: string;
  entitlementSoxSeverity?: string;
  entitlementSysSeverity?: string;
  entitlementPrivilegeSeverity?: string;
  lastUpdated?: string;
  entitlementDescription?: string | null;
  glossary?: string | null;
  customProperty1?: string | null;
  entitlementValueKey?: number | null;
}

export interface EntitlementSidePanelGroupRowContextState {
  current: EntitlementSidePanelRowContextState;
  prev?: EntitlementSidePanelRowContextState;
  next?: EntitlementSidePanelRowContextState;
}

interface EntitlementSidePanelState {
  isSidePanelVisible: boolean;
  rowContext: EntitlementSidePanelGroupRowContextState;
}

export interface EntitlementState {
  isLoading: boolean;
  controls: EntitlementControlsState;
  list: EntitlementListState;
  bulkaction: EntitlementBulkActionState;
  sidepanel: EntitlementSidePanelState;
}
